import PageHero from "../components/PageHero";
import { Search, PackageCheck, Ship, FlaskConical, FileCheck2, Warehouse, Headset } from "lucide-react";
import { Link } from "react-router-dom";

const SERVICES = [
  {
    icon: Search,
    title: "Global Sourcing",
    desc: "We identify, evaluate, and negotiate with qualified international principals to bring you the exact grade, spec and price point your formulation demands.",
    points: ["Principal scouting & audits", "Grade & spec matching", "Competitive price discovery"],
  },
  {
    icon: Ship,
    title: "Importing & Logistics",
    desc: "End-to-end import execution — from PI to your warehouse — with transparent tracking at every stage of the journey.",
    points: ["Documentation & INCO-terms", "Customs clearance", "Temperature-controlled shipping"],
  },
  {
    icon: FlaskConical,
    title: "Technical Support",
    desc: "Our in-house applications team helps you evaluate samples, troubleshoot formulations and identify suitable substitutes.",
    points: ["Formulation consultation", "Alternate ingredient suggestions", "Sample-to-scale guidance"],
  },
  {
    icon: FileCheck2,
    title: "Regulatory & Documentation",
    desc: "Complete paperwork — COA, MSDS, IFRA, allergen, Halal, Kosher and more — delivered with every consignment.",
    points: ["COA & batch traceability", "Regulatory filings assist", "Certification matching"],
  },
  {
    icon: Warehouse,
    title: "Warehousing & Stock",
    desc: "Strategic stocking of fast-moving materials for JIT availability, protecting your production timelines.",
    points: ["Buffer stocking", "Just-in-time dispatch", "PAN-India delivery"],
  },
  {
    icon: Headset,
    title: "Account Management",
    desc: "A dedicated point of contact who understands your formulations, ingredients and procurement rhythm.",
    points: ["Single-window coordination", "Proactive communication", "Issue resolution in 24h"],
  },
];

export default function ServicesPage() {
  return (
    <>
      <PageHero
        eyebrow="Our services"
        title="Sourcing is only the beginning."
        description="From the first inquiry to the last delivery, Goyam is an extension of your procurement and R&D teams — handling the complexity, so you can focus on creating."
        image="https://images.pexels.com/photos/6169056/pexels-photo-6169056.jpeg?auto=compress&cs=tinysrgb&w=2400"
      />

      <section className="section-y">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-line">
            {SERVICES.map((s) => (
              <div
                key={s.title}
                data-testid={`service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-brand-surface p-10 hover:bg-brand-surfaceAlt/50 transition-colors flex flex-col gap-5"
              >
                <s.icon className="w-7 h-7 text-brand-terracotta" strokeWidth={1.5} />
                <h3 className="font-serif text-3xl leading-tight">{s.title}</h3>
                <p className="text-brand-muted leading-relaxed">{s.desc}</p>
                <ul className="mt-auto space-y-2 pt-4 border-t border-brand-line">
                  {s.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-3 text-sm">
                      <PackageCheck className="w-4 h-4 text-brand-moss" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-y bg-brand-ink text-white">
        <div className="container-x grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <p className="eyebrow !text-brand-gold mb-4">Why Goyam</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              A sourcing partner that thinks like a formulator.
            </h2>
            <p className="mt-6 text-white/75 leading-relaxed max-w-xl">
              We understand INCI names, the quirks of emulsion systems, and why
              batch-to-batch color variance matters for your pigmented products.
              That's what makes us different from a typical trader.
            </p>
          </div>
          <div className="md:col-span-5">
            <Link to="/contact" className="btn-terracotta w-full justify-center text-base" data-testid="services-cta">
              Request a consultation
            </Link>
            <p className="mt-4 text-xs text-white/50 text-center tracking-wide">
              Free for qualified brands & manufacturers
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
